import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Alert, Severity } from '@energy-advice/alertfan-api-client';
import { Tooltip } from 'react-tooltip';
import { formatTime, primereactDataTableSortDateFunction } from 'utils/time-utils';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useGetAlertFan } from 'api/api-hooks/alert-fan-hooks';
import { stringArrayToTags } from '../../basic/strings-array-to-tags/StringArrayToTags';
import ParametersTableBody from './ParametersTableBody';
import TagsHistory from './TagsHistory';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';

import './AlertFan.scss'

type AlertsViewProps = {
    itemsPerPage?: number;
    updateFrequency?: number;
    topicTags?: Array<string>;
    itemTags?: Array<string>;
    hiddenTopicTags?: Array<string>;

    setTags: (tags: { topics?: Array<string>; items?: Array<string> }) => void;
    setTab: (n: number) => void;
};

const SeverityMap: Record<Severity, any> = {
    SEVERE: <i data-tooltip-content={'Severe'} style={{ color: 'red' }} className='pi pi-exclamation-triangle severity-body-tooltip'></i>,
    INFO: <i data-tooltip-content={'Info'} style={{ color: '#3B82F6' }} className='pi pi-info-circle severity-body-tooltip'></i>,
    DEBUG: <i data-tooltip-content={'Debug'} className='fa fa-solid fa-bug severity-body-tooltip'></i>,
    WARNING: <i data-tooltip-content={'Warning'} style={{ color: 'orange' }} className='pi pi-exclamation-triangle severity-body-tooltip'></i>,
};

const AlertsView = ({ updateFrequency, itemsPerPage, setTags, setTab, topicTags, itemTags, hiddenTopicTags }: AlertsViewProps) => {
    const { data, refetch, isLoading, error } = useGetAlertFan(new Date().toISOString(), { refetchInterval: updateFrequency ? updateFrequency * 1000 : undefined, gcTime: 0 });

    const [filterableTopicTags, setFilterableTopicTags] = useState([]);
    const [filterableItemTags, setFilterableItemTags] = useState([])

    const [topicTagsFilterValue, setTopicTagsFilterValue] = useState([])
    const [itemTagsFilterValue, setItemTagsFilterValue] = useState([])

    const [initialTopicTagsFilter] = useState(topicTags ?? []);
    const [initialItemTagsFilter] = useState(itemTags ?? [])
    const [filteredData, setFilteredData] = useState([]);

    let customData = [];
    useEffect(() => {
        if (!data) return;

        customData = hiddenTopicTags && hiddenTopicTags.length > 0
        ? data.filter(alert => !alert.topicTags.some(tag => hiddenTopicTags.includes(tag)))
        : data;

        const filteredData = customData
            .filter((alert) => {
                if (initialTopicTagsFilter.length === 0) return true;
                return alert.topicTags.some((tag) => initialTopicTagsFilter.includes(tag));
            })
            .filter((alert) => {
                if (initialItemTagsFilter.length === 0) return true;
                return alert.itemTags.some((tag) => initialItemTagsFilter.includes(tag));
            });

        const allTopicTags = new Set();
        const allItemTags = new Set();

        customData.map((obj) => {
            obj.topicTags.map((tag) => allTopicTags.add(tag));
            obj.itemTags.map((tag) => allItemTags.add(tag));
        });

        const topicTagsTemp = [...allTopicTags].map(tag => ({ tag }));
        const itemTagsTemp = [...allItemTags].map(tag => ({ tag }));

        if (topicTags) setTopicTagsFilterValue(topicTags);
        if (itemTags) setItemTagsFilterValue(itemTags);

        setFilterableTopicTags(topicTagsTemp);
        setFilterableItemTags(itemTagsTemp);
        setFilteredData(filteredData);
    }, [data]);

    const applyTopicFilters = (value) => {
        applyFilters('topicTags', value);
    };

    const applyItemFilters = (value) => {
        applyFilters('itemTags', value);
    };

    const applyFilters = (filterType, value) => {
        const filteredData = customData.filter((alert) => {
            if (value.length === 0) return true;

            if (filterType === 'topicTags') {
                return alert.topicTags.some((tag) => value.includes(tag));
            } else if (filterType === 'itemTags') {
                return alert.itemTags.some((tag) => value.includes(tag));
            }

            return true;
        });

        if (filterType === 'topicTags') {
            setTopicTagsFilterValue(value);
        } else if (filterType === 'itemTags') {
            setItemTagsFilterValue(value);
        }

        setFilteredData(filteredData);
    };

    const rowsPerPageOptions = itemsPerPage
        ? [itemsPerPage, 10, 20, 30]
              .sort((a, b) => a - b)
              .filter(function (x, i, a) {
                  return a.indexOf(x) == i;
              })
        : [10, 20, 30];

    const severityBody = (rowData: Alert) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip anchorSelect='.severity-body-tooltip' delayShow={200} />
                {rowData.severity ? SeverityMap[rowData.severity] : <></>}
            </div>
        );
    };

    const severityFilter = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <div className={'flex flex-column gap-2'}>
                {Object.values(Severity).map((severity) => {
                    return (
                        <div key={severity} className='flex gap-1 align-items-center'>
                            <Checkbox inputId={severity} name='category' value={severity} onChange={(e) => options.filterCallback(e.value)} checked={options.value === severity} />
                            {SeverityMap[severity]}
                            <label htmlFor={severity}>{severity}</label>
                        </div>
                    );
                })}
            </div>
        );
    };

    const textBody = (rowData) => {
        return <div dangerouslySetInnerHTML={{ __html: rowData.text }}></div>;
    };

    const historyAlertBody = (rowData: Alert) => {
        const [historyDialog, setHistoryDialog] = useState(false);
        return (
            <>
                <Button className={'p-button-sm'} icon={'pi pi-history'} onClick={() => setHistoryDialog(true)} tooltip={'Alert History'} />
                {historyDialog && <TagsHistory tags={[...rowData.itemTags, ...rowData.topicTags]} dialog={historyDialog} showDialog={setHistoryDialog} />}
            </>
        );
    };

    if (error) {
        return <div style={{ border: '1px solid red' }}>Error while fetching alerts...</div>;
    }

    const customTagsFilter = (rowData, filterValue) => {
        if (filterValue !== null && filterValue.length > 0) {
            return Array.isArray(filterValue) && Array.isArray(rowData) && rowData.some((rowItem) => filterValue.includes(rowItem));
        } else {
            return true;
        }
    };

    const tagItemTemplate = (option) => {
        let truncatedString = option.tag;
        if (truncatedString.length > 12) {
            truncatedString = option.tag.substring(0, 25) + '...';
        }
        return (
            <div key={option.tag}>
                <Tooltip target='.tag-tooltip' />
                <Tag value={truncatedString} className='tag-tooltip' data-pr-tooltip={option.tag} rounded></Tag>
            </div>
        );
    };

    const topicTagsFilterChangeHandler = (e, options) => {
        options.filterCallback(e.value)
        applyTopicFilters(e.value)
    }

    const itemTagsFilterChangeHandler = (e, options) => {
        options.filterCallback(e.value)
        applyItemFilters(e.value)
    }

    const topicTagsRowFilterTemplate = (options) => {
        return (
            <MultiSelect
                value={topicTagsFilterValue}
                options={filterableTopicTags}
                onChange={(e) => topicTagsFilterChangeHandler(e, options)}
                optionLabel='tag'
                optionValue='tag'
                placeholder='Tag'
                emptyFilterMessage={() => <div>No tags to show</div>}
                filter
                itemTemplate={tagItemTemplate}
                maxSelectedLabels={0}
            />
        );
    };

    const itemTagsRowFilterTemplate = (options) => {
        return (
            <MultiSelect
                value={itemTagsFilterValue}
                options={filterableItemTags}
                onChange={(e) => itemTagsFilterChangeHandler(e, options)}
                optionLabel='tag'
                optionValue='tag'
                placeholder='Tag'
                emptyFilterMessage={() => <div>No tags to show</div>}
                filter
                itemTemplate={tagItemTemplate}
                maxSelectedLabels={0}
            />
        );
    };

    return (
        <div className={'flex flex-column gap-2'}>
            <div className={'flex justify-content-end'}>
                <Button icon={'pi pi-refresh'} className={'p-button-sm'} onClick={() => refetch()} />
            </div>
            <DataTable
                value={filteredData}
                loading={isLoading}
                paginator={filteredData ? filteredData.length > 10 : false}
                responsiveLayout='scroll'
                paginatorTemplate='CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown'
                currentPageReportTemplate='Showing {first} to {last} of {totalRecords}'
                rows={itemsPerPage || 10}
                rowsPerPageOptions={rowsPerPageOptions}
                removableSort
                size={'small'}
            >
                <Column
                    header={''}
                    field={'severity'}
                    style={{ width: 0 }}
                    sortable
                    filter
                    filterMatchMode={'contains'}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterElement={severityFilter}
                    body={severityBody}
                    headerTooltip={'Severity'}
                    headerTooltipOptions={{ position: 'top' }}
                />
                <Column
                    header={'Text'}
                    field={'text'}
                    sortable
                    filter
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={'Search'}
                    filterField='text'
                    filterMatchMode={'contains'}
                    body={textBody}
                />
                <Column
                    header={'Topic Tags'}
                    headerClassName={topicTags && topicTags.length > 0 ? "filtered" : ""}
                    field={'topicTags'}
                    body={(e: Alert) => stringArrayToTags(e.topicTags)}
                    filter
                    filterMatchMode={'custom'}
                    filterElement={topicTagsRowFilterTemplate}
                    filterFunction={customTagsFilter}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={'Tag name'}
                />
                <Column
                    header={'Item Tags'}
                    headerClassName={itemTags && itemTags.length > 0 ? "filtered" : ""}
                    field={'itemTags'}
                    body={(e: Alert) => stringArrayToTags(e.itemTags)}
                    filter
                    filterMatchMode={'custom'}
                    filterElement={itemTagsRowFilterTemplate}
                    filterFunction={customTagsFilter}
                    showFilterMatchModes={false}
                    showFilterMenuOptions={false}
                    filterPlaceholder={'Tag name'}
                />
                <Column header={'Started'} field={'startsAt'} body={(rowData) => formatTime(rowData.startsAt)} sortable sortFunction={primereactDataTableSortDateFunction} />
                <Column style={{ width: 0 }} body={(rowData) => <ParametersTableBody properties={rowData.properties} />} />
                <Column
                    style={{ width: '0' }}
                    body={(rowData) => (
                        <Button
                            icon={'pi pi-plus'}
                            className={'p-button-sm'}
                            onClick={() => {
                                setTab(1);
                                setTags({
                                    items: rowData.itemTags,
                                    topics: rowData.topicTags,
                                });
                            }}
                            tooltip={'Create Silence'}
                        />
                    )}
                />
                <Column body={historyAlertBody} style={{ width: '0' }} />
            </DataTable>
        </div>
    );
};

export default AlertsView;
